import { useState } from "react";
import { withNamespaces } from "react-i18next";
import { Alert, Card, CardBody, Col, Row } from "reactstrap";
import DeadlineManagementCard from "./components/deadline-management-card";
import RequestClarificationModal from "./components/request-clarification-modal";
import ResponseDeadline from "./components/response-deadline";
import ResumeWorkflowModal from "./components/resume-workflow-modal";
import { useDsrDeadlineActions } from "./hooks/useDsrDeadlineActions";
import { useDsrDeadlineWorkflow } from "./hooks/useDsrDeadlineWorkflow";
import { canEditDsrDeadline } from "./utils";

const DeadlineManagement = ({ t, caseId, report }) => {
  const { isFeatureEnabled, isDeadlineManagementEnabled, isLoading, isError, data } =
    useDsrDeadlineWorkflow(caseId);
  const { startWaitingPeriod, resumeWorkflow } = useDsrDeadlineActions(caseId, t);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [isResumeModalOpen, setIsResumeModalOpen] = useState(false);
  const canEdit = canEditDsrDeadline(report);

  if (!isFeatureEnabled) {
    return null;
  }

  if (isLoading) {
    return (
      <Row className="g-3">
        <Col xs="12" md="6">
          <div
            className="dt-field dt-skeleton"
            style={{ width: "100%", height: "160px" }}
          />
        </Col>
        <Col xs="12" md="6">
          <div
            className="dt-field dt-skeleton"
            style={{ width: "100%", height: "160px" }}
          />
        </Col>
      </Row>
    );
  }

  if (isError) {
    return (
      <Alert color="warning" className="mb-0">
        {t("An error occurred while fetching the deadline.")}
      </Alert>
    );
  }

  if (!isDeadlineManagementEnabled) {
    return null;
  }

  return (
    <>
      <RequestClarificationModal
        isOpen={isRequestModalOpen}
        isLoading={startWaitingPeriod.isLoading}
        onClose={() => setIsRequestModalOpen(false)}
        onConfirm={(payload) => {
          startWaitingPeriod.mutate(payload, {
            onSuccess: () => setIsRequestModalOpen(false),
          });
        }}
      />
      <ResumeWorkflowModal
        isOpen={isResumeModalOpen}
        isLoading={resumeWorkflow.isLoading}
        onClose={() => setIsResumeModalOpen(false)}
        onConfirm={() => {
          resumeWorkflow.mutate(undefined, {
            onSuccess: () => setIsResumeModalOpen(false),
          });
        }}
      />
      <div className="dsr-deadline-cards">
        <Card className="dsr-deadline-card">
          <CardBody>
            <h6 className="dsr-deadline-card__title">
              {t("Response Deadline & Deadline Management")}
            </h6>
            <p className="dsr-deadline-card__note">
              {t(
                "View statutory and effective deadlines and manage formal deadline extensions for this request."
              )}
            </p>
            <div className="dsr-deadline-card__main">
              <div className="dsr-deadline-card__deadlines">
                <ResponseDeadline workflow={data} />
              </div>
              <div className="dsr-deadline-card__aside">
                <DeadlineManagementCard
                  workflow={data}
                  canEdit={canEdit}
                  onExtend={() => setIsRequestModalOpen(true)}
                  onResume={() => setIsResumeModalOpen(true)}
                />
              </div>
            </div>
          </CardBody>
          <div className="dsr-deadline-card__footer">
            <i aria-hidden="true" className="fa fa-info-circle" />
            <span>
              {t(
                "No formal deadline extension has been applied. The original statutory deadline remains applicable."
              )}
            </span>
          </div>
        </Card>
      </div>
    </>
  );
};

export default withNamespaces()(DeadlineManagement);
